/**
 * PropertiesDialog - Dialog for viewing custom properties in a table
 * Similar to variables tab - shows table with Edit buttons
 */

import type { Component } from 'react';
import { CustomProperty } from '../models/CustomProperty';
import Dialog from './Dialog';

// React is available globally from CDN
const React = (window as any).React;
const ReactComponent = React.Component;

// Forward declarations
declare const cheatFreezes: {
    customProperties: { [entityId: string]: { [propertyName: string]: boolean } };
};

interface PropertiesDialogProps {
    entityType: 'room' | 'object' | 'character' | 'variable' | 'player';
    entityId: string;
    entityName: string;
    properties: CustomProperty[];
    onSave: (properties: CustomProperty[]) => void;
    onCancel: () => void;
}

interface PropertiesDialogState {
    refreshKey: number;
}

class PropertiesDialog extends ReactComponent<PropertiesDialogProps, PropertiesDialogState> {
    constructor(props: PropertiesDialogProps) {
        super(props);
        this.state = {
            refreshKey: 0
        };
    }

    openPropertyEditDialog = (property: CustomProperty): void => {
        const dialogRoot = document.getElementById('property-edit-dialog-root');
        if (!dialogRoot) return;

        const ReactDOM = (window as any).ReactDOM;
        const PropertyEditDialog = (window as any).PropertyEditDialog;
        if (!ReactDOM || !PropertyEditDialog) return;

        // Initialize cheatFreezes.customProperties if needed
        if (!cheatFreezes.customProperties) {
            (cheatFreezes as any).customProperties = {};
        }
        if (!cheatFreezes.customProperties[this.props.entityId]) {
            cheatFreezes.customProperties[this.props.entityId] = {};
        }

        const handleSave = (value: string, frozen: boolean) => {
            // Update property value
            property.Value = value;
            
            // Update freeze state
            if (frozen) {
                cheatFreezes.customProperties[this.props.entityId][property.Name] = true;
            } else {
                delete cheatFreezes.customProperties[this.props.entityId][property.Name];
            }

            // Close the edit dialog only
            if (ReactDOM.createRoot) {
                const root = (window as any).propertyEditDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }

            // Force refresh of the properties table (but keep PropertiesDialog open)
            this.setState({ refreshKey: this.state.refreshKey + 1 });
        };

        const handleCancel = () => {
            if (ReactDOM.createRoot) {
                const root = (window as any).propertyEditDialogRoot;
                if (root) root.render(null);
            } else {
                ReactDOM.render(null, dialogRoot);
            }
        };

        if (ReactDOM.createRoot) {
            let root = (window as any).propertyEditDialogRoot;
            if (!root) {
                root = ReactDOM.createRoot(dialogRoot);
                (window as any).propertyEditDialogRoot = root;
            }
            root.render(React.createElement(PropertyEditDialog, {
                entityId: this.props.entityId,
                property: property,
                onSave: handleSave,
                onCancel: handleCancel
            }));
        } else {
            ReactDOM.render(
                React.createElement(PropertyEditDialog, {
                    entityId: this.props.entityId,
                    property: property,
                    onSave: handleSave,
                    onCancel: handleCancel
                }),
                dialogRoot
            );
        }
    };

    handleCancel = (): void => {
        this.props.onCancel();
    };

    render(): any {
        const DialogComponent = (window as any).Dialog;
        if (!DialogComponent) return null;

        // Initialize cheatFreezes.customProperties if needed
        if (!cheatFreezes.customProperties) {
            (cheatFreezes as any).customProperties = {};
        }
        if (!cheatFreezes.customProperties[this.props.entityId]) {
            cheatFreezes.customProperties[this.props.entityId] = {};
        }

        if (!this.props.properties || this.props.properties.length === 0) {
            return React.createElement(DialogComponent, {
                title: `Properties: ${this.props.entityName}`,
                onClose: this.handleCancel,
                closeOnBackdropClick: true,
                className: 'properties-dialog',
                maxWidth: '90vw'
            },
                React.createElement('div', { style: { padding: '20px', textAlign: 'center' } },
                    React.createElement('p', null, 'No custom properties found.')
                )
            );
        }

        // Sort properties alphabetically
        const sortedProperties = [...this.props.properties].sort((a, b) => 
            a.Name.toLowerCase().localeCompare(b.Name.toLowerCase())
        );

        const renderRow = (property: CustomProperty) => {
            const isFrozen = !!(cheatFreezes.customProperties[this.props.entityId][property.Name]);
            const value = property.Value || '';
            
            return React.createElement('tr', { 
                key: property.Name,
                style: { height: '70px' }
            },
                React.createElement('td', {
                    style: {
                        height: '70px',
                        lineHeight: '1.33rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }
                },
                    React.createElement('span', null, property.Name),
                    isFrozen && React.createElement('span', {
                        style: {
                            marginLeft: '8px',
                            color: '#ff6b6b',
                            fontSize: '12px'
                        },
                        title: 'Frozen'
                    }, '🔒')
                ),
                React.createElement('td', {
                    style: {
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        height: '70px',
                        lineHeight: '1.33rem'
                    }
                },
                    React.createElement('span', {
                        style: {
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            height: '2.8rem',
                            lineHeight: '1.33rem'
                        }
                    }, value)
                ),
                React.createElement('td', {
                    style: {
                        height: '70px',
                        lineHeight: '1.33rem'
                    }
                },
                    React.createElement('button', {
                        className: 'btn',
                        onClick: () => {
                            this.openPropertyEditDialog(property);
                        }
                    }, 'Edit')
                )
            );
        };

        const colgroup = React.createElement('colgroup', null,
            React.createElement('col', { style: { width: '100%' } }), // Name - takes remaining space
            React.createElement('col', { style: { width: '8rem' } }), // Value - same width as variables tab
            React.createElement('col', { style: { width: '80px' } }) // Actions
        );

        const tableHeaders = React.createElement('thead', null,
            React.createElement('tr', null,
                React.createElement('th', null, 'Name'),
                React.createElement('th', null, 'Value'),
                React.createElement('th', null, '') // Empty header for actions column
            )
        );

        const PaginatedTable = (window as any).PaginatedTable;

        return React.createElement(DialogComponent, {
            title: `Properties: ${this.props.entityName}`,
            onClose: this.handleCancel,
            closeOnBackdropClick: true,
            className: 'properties-dialog',
            maxWidth: '90vw'
        },
            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', height: '100%', gap: '16px' } },
                PaginatedTable ? React.createElement(PaginatedTable, {
                    key: `properties-table-${this.state.refreshKey}`,
                    items: sortedProperties,
                    renderRow: renderRow,
                    headers: tableHeaders,
                    colgroup: colgroup,
                    minItemsPerPage: 5,
                    rowHeight: 70,
                    maxHeight: '100%',
                    className: 'properties-table'
                }) : React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse' } },
                    colgroup,
                    tableHeaders,
                    React.createElement('tbody', null,
                        sortedProperties.map(property => renderRow(property))
                    )
                )
            )
        );
    }
}

(window as any).PropertiesDialog = PropertiesDialog;
export default PropertiesDialog;
